import React, { FC, useCallback, useEffect } from "react";
import { Switch, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { skipToken } from "@reduxjs/toolkit/query";
import { useAppDispatch, useAppSelector } from "../../../../app";
import { setAnnotationsViewMode } from "../../../../app/store/appSlice";
import { useGetAnnotationSnapshotQuery } from "../../../../documentSet/store/documentSetApi";
import {
  EXPORTED_VERSION,
  selectHasSnapshot,
} from "../../../../annotation/selectors/annotationReducerSelector";
import useAnnotationViewGroupBlockCounts from "../../../hooks/useAnnotationViewGroupBlockCounts";
import "./AnnotationViewToggle.scss";

/**
 * Switches the detail page between the annotations as they were exported and the current
 * ones. A document always opens on its current annotations, the latest version being what
 * you want to see. Only the view changes; editing always targets the current ones.
 *
 * Holds the subscription on the snapshot so it stays cached while it is on screen.
 */
const AnnotationViewToggle: FC = () => {
  const { t } = useTranslation("annotationSideControls");
  const dispatch = useAppDispatch();

  const documentSetId = useAppSelector(
    (state) => state.documentSetsReducer.activeDocumentSet?.id,
  );
  const annotationsViewMode = useAppSelector(
    (state) => state.appReducer.annotationsViewMode,
  );
  const editAfterExportMode = useAppSelector(
    (state) => state.appReducer.editAfterExportMode,
  );
  const hasBeenEdited = useAppSelector(
    (state) => state.appReducer.hasBeenEdited,
  );
  const hasSnapshot = useAppSelector(selectHasSnapshot);

  const showingExported = annotationsViewMode === "exported";

  const { isFetching, isError } = useGetAnnotationSnapshotQuery(
    hasSnapshot && documentSetId
      ? { documentSetId, version: EXPORTED_VERSION }
      : skipToken,
  );

  useAnnotationViewGroupBlockCounts();

  // The view mode is app-wide state, so without this a document set opened after one left
  // on "exported" would open on "exported" too.
  useEffect(() => {
    dispatch(setAnnotationsViewMode("edited"));
  }, [dispatch, documentSetId]);

  const handleChange = useCallback(
    (checked: boolean) => {
      dispatch(setAnnotationsViewMode(checked ? "edited" : "exported"));
    },
    [dispatch],
  );

  if (!hasSnapshot) {
    return null;
  }

  // Switching to the read-only exported view hides the page's save prompts, so unsaved work
  // would disappear on the next navigation.
  const isDisabled = editAfterExportMode || hasBeenEdited;

  const getTooltip = () => {
    if (isDisabled) {
      return t("annotationViewToggle.disabledWhileEditing");
    }

    if (isError) {
      return t("annotationViewToggle.loadFailed");
    }

    return showingExported
      ? t("annotationViewToggle.exportedTooltip")
      : t("annotationViewToggle.editedTooltip");
  };

  return (
    <Tooltip title={getTooltip()}>
      <Switch
        className="annotation-view-toggle"
        checked={!showingExported}
        loading={isFetching}
        disabled={isDisabled}
        onChange={handleChange}
        checkedChildren={t("annotationViewToggle.editedShort")}
        unCheckedChildren={t("annotationViewToggle.exportedShort")}
      />
    </Tooltip>
  );
};

export default AnnotationViewToggle;
